/**
 * What the builder's save and the list switch both lean on: the PATCH, the
 * backfill announcement with its Undo, and the order in which confirmations
 * were raised. No flow state lives here. Whether a builder session CREATED its
 * rule is `useBuilderSave`'s, and so is the saved toast that words it; the
 * switch has its own wording, so neither can speak in the other's voice.
 */
import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { useToast } from '../../platform/feedback/ToastProvider'
import { count } from './applyCopy'
import { categoryOf, type UndoTarget } from './saveShared'
import { useRevertBatch, useUpdateRule, type Reverted, type SaveResult } from './useRules'

const txns = (n: number) => `${count(n)} transaction${n === 1 ? '' : 's'}`

/**
 * The fields a revert left, in field units (the wire's `skipped_*` count CHANGES):
 * "Left 1 field as it is because it changed since." / "Left 3 fields as they are: 1 changed since and
 * 2 you set yourself." '' when it left none.
 */
function leftFields({ changed, locked }: Reverted): string {
  const n = changed + locked
  if (n === 0) return ''
  const lead = n === 1 ? 'Left 1 field as it is' : `Left ${count(n)} fields as they are`
  if (locked === 0) return `${lead} because ${n === 1 ? 'it' : 'they'} changed since.`
  if (changed === 0) return `${lead} because you set ${n === 1 ? 'it' : 'them'} yourself.`
  return `${lead}: ${count(changed)} changed since and ${count(locked)} you set yourself.`
}

/**
 * The toast after Undo, in the batch revert's own units (rv-review I2):
 *
 * - `reverted` and `failed` count TRANSACTIONS (log entries): `reverted` only those whose every
 *   change was undone, `failed` those the server refused outright.
 * - `skipped_changed` / `skipped_locked` count FIELDS left alone.
 *
 * A transaction with a field left is neither: its other changes WERE undone, but the response does
 * not count it. So `reverted: 0` beside a skip is never "Nothing was undone"; the toast says what was
 * left, and, when nothing was refused, that everything else was undone (true of every log that was
 * not refused: each change is undone unless it is one of the skips).
 */
export function undidMessage(r: Reverted): string {
  const left = leftFields(r)
  if (left === '' && r.failed === 0) {
    return r.reverted === 0 ? 'There was nothing left to undo.' : `Undid the changes to ${txns(r.reverted)}.`
  }
  const sentences: string[] = []
  if (r.reverted > 0) sentences.push(left === '' ? `Undid the changes to ${txns(r.reverted)}.` : `Fully undid ${txns(r.reverted)}.`)
  if (left !== '') sentences.push(left)
  if (r.failed > 0) {
    sentences.push(
      `Couldn't undo ${txns(r.failed)}: the category or merchant ${r.failed === 1 ? 'it' : 'they'} had before, or the transaction itself, is no longer available to you.`,
    )
  } else {
    sentences.push('Everything else was undone.')
  }
  return sentences.join(' ')
}

/** A refusal is a warning; a field left alone, which the user may expect, is information. */
const undidKind = (r: Reverted) => (r.failed > 0 ? 'warning' : r.changed + r.locked > 0 ? 'info' : 'success')

export function useRuleBase() {
  const client = useQueryClient()
  const update = useUpdateRule()
  const revert = useRevertBatch()
  const toast = useToast()
  const [undo, setUndo] = useState<UndoTarget | null>(null)
  /** Hands out `Offer.raised`. */
  const raisedSeq = useRef(0)

  /**
   * The toast for any write whose response carries a batch that changed
   * something — the confirmed flip, but equally a save the client predicted
   * would not backfill, a count-0 flip whose count moved, or the switch. The
   * server wrote to history either way, so Undo is offered either way.
   * Returns false when there was nothing to announce.
   */
  function announceBackfill(result: SaveResult): boolean {
    const { backfill, rule } = result
    if (backfill === null || backfill.modified <= 0) return false
    const batchId = backfill.batch_id
    toast.show({
      kind: 'success',
      message:
        categoryOf(rule) !== null
          ? `Categorised ${backfill.modified} past transaction${backfill.modified === 1 ? '' : 's'} with “${rule.rule.name}”`
          : `Updated ${backfill.modified} past transaction${backfill.modified === 1 ? '' : 's'}`,
      action: { label: 'Undo', onAct: () => setUndo({ batchId }) },
    })
    return true
  }

  /** The Undo toast's confirmation. Resolves on success; a rejection stays
   *  in the dialog, which shows it. */
  async function revertBatch() {
    if (undo === null) return
    const result = await revert.mutateAsync(undo.batchId)
    setUndo(null)
    toast.show({ kind: undidKind(result), message: undidMessage(result) })
  }

  return {
    client,
    update,
    toast,
    undo,
    /** Raise the Undo confirmation for a batch some other write made (Apply now, Apply to selected). */
    offerUndo: (batchId: string) => setUndo({ batchId }),
    raised: () => ++raisedSeq.current,
    announceBackfill,
    revertBatch,
    closeUndo: () => setUndo(null),
  }
}

export type RuleBase = ReturnType<typeof useRuleBase>
